/**
 * Persisted user settings, storm mode, energy decay, places, notification classes, and
 * the vocabulary loop.
 *
 * Authorised by: product-concept §5 (warm cap, storm mode, hard deadlines get
 * "calendar-grade treatment and escalating, user-configured reminders"), §7.2
 * (vocabulary corrections train per-user), §8.1 (surfacing axes), §9.2 (energy states),
 * §11 (sensory settings, literal-language mode, notification ceiling),
 * implementation-brief §3 (geofence evaluation on-device only) and §6 (per-user
 * vocabulary correction loop is phase-1 mandatory).
 *
 * Everything the user can control lives in ONE shape, because the Intensity dial's
 * per-channel overrides, the density setting and the literal register all need to be
 * resolved together to produce a theme, and splitting them across stores is how a
 * setting ends up applied on four screens out of five.
 */

import type { IntensityPreferences } from './intensity';
import { DEFAULT_INTENSITY_PREFERENCES } from './intensity';
// The material choice (design-direction §2, §7; product-concept §12 as amended by
// docs/DEVIATIONS.md M11). Lives beside `scheme`/`density` below: all three are the
// same kind of rendering choice, and none of the three can move a sensory outcome
// (see `materials.ts`'s header for the argument that a material is colour only).
import type { MaterialId } from './materials';
import { DEFAULT_MATERIAL } from './materials';
// The surface treatment (RULINGS R188). Free at every tier by construction — see
// `treatments.ts` for why charging for one would unprice the four paid materials.
import type { Treatment } from './treatments';
import { DEFAULT_TREATMENT } from './treatments';
// The home-screen icon (RULINGS R197). Structurally a `MaterialId`, and free at every tier
// however its material is priced — see `appIcons.ts` for both arguments.
import type { AppIcon } from './appIcons';
import { DEFAULT_APP_ICON } from './appIcons';
import type { CapsuleImagery, EnergyLevel } from './model';
// The daily suggestion's stored preference (product-concept §8.1, §9.2). The type and its
// only writer live in `./today.ts` beside the selection they govern; this file holds the
// field, exactly as it holds `sortingPrompt` for the other once-a-day invitation.
import type { TodaySettings } from './today';
import { DEFAULT_TODAY } from './today';
// The widget privacy policy (docs/specs/widgets-architecture.md §5; product-concept §14;
// implementation-brief §3). The type, its default and its normaliser live in `./widgets.ts`
// beside the projection they govern — the same arrangement `TodaySettings` has, and here it
// is also structural: `widgets.ts` cannot import `Settings` back without a cycle.
import type { WidgetPrivacy } from './widgets';
import { DEFAULT_WIDGET_PRIVACY } from './widgets';

// ---------------------------------------------------------------------------
// Places — coarse labels only
// ---------------------------------------------------------------------------

/**
 * A registered place. PRIVACY INVARIANT (implementation-brief §3, objection 2):
 * "geofence evaluation happens on-device only... the server never receives raw
 * location." Consequently this type carries NO latitude or longitude fields. The OS
 * geofence registration holds the coordinates; Carn's synced model holds a label and
 * an opaque OS region identifier.
 *
 * If you need coordinates, they belong in a device-local table that the sync engine
 * has no route for. Do not add them here.
 */
export type Place = {
  readonly id: string;
  readonly label: string;
  /** Opaque OS region identifier, device-local in practice. Never a coordinate pair. */
  readonly regionRef?: string;
  readonly kind: 'home' | 'store' | 'work' | 'other';
};

/**
 * "A handful of registered regions — user's stores, home — not continuous tracking"
 * (implementation-brief §8). iOS caps monitored regions at 20; we stay well under, both
 * for battery and because a long list of tracked places is the surveillance posture
 * product-concept §11 refuses.
 */
export const MAX_REGISTERED_PLACES = 8;

// ---------------------------------------------------------------------------
// Energy
// ---------------------------------------------------------------------------

/**
 * "Never asked more than once a day, never mandatory, decays gracefully if unset"
 * (product-concept §9.2). Objection 2 in that section accepts that users stop logging
 * within a week — hence no feature may depend on energy existing.
 */
export const ENERGY_VALID_HOURS = 20;

export type EnergyLog = { readonly level: EnergyLevel; readonly at: number };

export type EnergySettings = {
  readonly asked: boolean;
  readonly lastAskedAt?: number;
  readonly lastAnsweredAt?: number;
  readonly lastLog?: EnergyLog;
};

/** Undefined is a first-class answer. Callers must handle it without degrading. */
export function currentEnergy(log: EnergyLog | undefined, now: number): EnergyLevel | undefined {
  if (!log) return undefined;
  return now - log.at <= ENERGY_VALID_HOURS * 60 * 60 * 1000 ? log.level : undefined;
}

/**
 * At most one ask per calendar day, and never on the day the user first opened the app —
 * first run is for reaching first capture in under sixty seconds
 * (implementation-brief §11), not for answering questions about yourself.
 *
 * The previous version's comment claimed "never on a first launch" while the code
 * returned `true` with everything undefined, and it required a `lastAnsweredAt` field
 * that `Settings.energy` did not have (critique: shape disagreement). Both are fixed:
 * the parameter is the settings sub-object itself, so they cannot drift again.
 */
export function shouldAskEnergy(args: {
  now: number;
  energy: EnergySettings;
  /** When the user first opened Carn. Undefined means "we do not know yet" — do not ask. */
  firstRunAt?: number;
  enabled: boolean;
}): boolean {
  if (!args.enabled) return false;
  if (args.firstRunAt === undefined) return false;
  const startOfToday = new Date(args.now).setHours(0, 0, 0, 0);
  if (args.firstRunAt >= startOfToday) return false;
  if ((args.energy.lastAskedAt ?? 0) >= startOfToday) return false;
  if ((args.energy.lastAnsweredAt ?? 0) >= startOfToday) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Vocabulary — "the girls" = the chickens
// ---------------------------------------------------------------------------

/**
 * Per-user vocabulary corrections (product-concept §7.2, implementation-brief §6:
 * "phase-1 mandatory, because no stock model knows a smallholding's private
 * language").
 *
 * Corrections are learned from the user's own edits, applied locally, and are the
 * user's data — never aggregated, never used to profile (§11: body-aware, not
 * surveillance).
 *
 * APPEND-ONLY (critique: "`recordCorrection` overwrites `meant` with no history"). The
 * entry is a log of what the user said this phrase means, so a mis-typed correction is
 * recoverable and `confirmations` cannot be forged — it is the log's length.
 */
export type VocabularyCorrection = {
  readonly meant: string;
  readonly at: number;
  /**
   * A TOMBSTONE. The user asked to forget this phrase.
   *
   * A rule in the private dictionary silently edits everything the user says afterwards,
   * so it has to be undoable — a review reproduced `'Buy 2 bags' → 'Buy 3 bags'` teaching
   * the app that 2 means 3, with the only remedy available to the user being to invent the
   * rule `2 → 2`. But the log is append-only by law (CLAUDE.md: "History is append-only";
   * this file: "so a mis-typed correction is recoverable"), so forgetting cannot be a
   * delete. It is an appended state that means "no current meaning": `currentMeaning`
   * returns `undefined`, `applyVocabulary` therefore stops substituting, and every
   * previous correction is still in the log where support and the user can see it.
   *
   * Present only when true, so an ordinary correction's shape is unchanged.
   */
  readonly forgotten?: true;
};

export type VocabularyEntry = {
  /** What the recogniser heard. */
  readonly heard: string;
  /** Append-only. The newest entry is what the phrase currently means. */
  readonly corrections: readonly VocabularyCorrection[];
};

function lastCorrection(entry: VocabularyEntry): VocabularyCorrection | undefined {
  return entry.corrections.length === 0
    ? undefined
    : entry.corrections[entry.corrections.length - 1];
}

export function currentMeaning(entry: VocabularyEntry): string | undefined {
  const last = lastCorrection(entry);
  if (!last || last.forgotten) return undefined;
  return last.meant;
}

/** True when the newest state in the log is a tombstone: the rule no longer fires. */
export function isForgotten(entry: VocabularyEntry): boolean {
  return lastCorrection(entry)?.forgotten === true;
}

/**
 * How many times the user has confirmed this. A fold, so it never decreases.
 *
 * A tombstone is not a confirmation, so it is not counted — otherwise forgetting a phrase
 * would report to the user as having taught it once more.
 */
export function confirmations(entry: VocabularyEntry): number {
  return entry.corrections.filter((c) => !c.forgotten).length;
}

export function updatedAt(entry: VocabularyEntry): number | undefined {
  const last =
    entry.corrections.length === 0
      ? undefined
      : entry.corrections[entry.corrections.length - 1];
  return last?.at;
}

/**
 * Apply the user's private language to a transcript.
 *
 * The replacement is a FUNCTION, not a string (critique: replacement-pattern injection).
 * Passing user text straight to `String.replace` means `$&`, `$1`, `$'` in what the user
 * typed are interpreted as replacement patterns, so a correction containing a dollar sign
 * silently mangled the transcript.
 */
export function applyVocabulary(text: string, entries: readonly VocabularyEntry[]): string {
  // Longest `heard` first, so "the girls" beats "girls".
  const sorted = [...entries].sort((a, b) => b.heard.length - a.heard.length);
  let out = text;
  for (const entry of sorted) {
    const meant = currentMeaning(entry);
    if (meant === undefined) continue;
    const pattern = new RegExp('\\b' + escapeRegex(entry.heard) + '\\b', 'gi');
    out = out.replace(pattern, () => meant);
  }
  return out;
}

export function recordCorrection(
  entries: readonly VocabularyEntry[],
  heard: string,
  meant: string,
  now: number,
): VocabularyEntry[] {
  const key = heard.trim().toLowerCase();
  const correction: VocabularyCorrection = { meant: meant.trim(), at: now };
  const existing = entries.find((e) => e.heard.trim().toLowerCase() === key);
  if (existing) {
    return entries.map((e) =>
      e === existing ? { ...e, corrections: [...e.corrections, correction] } : e,
    );
  }
  return [...entries, { heard: heard.trim(), corrections: [correction] }];
}

/**
 * Stop a rule from firing, without deleting anything.
 *
 * Appends a tombstone state to the entry's log (see `VocabularyCorrection.forgotten`). The
 * phrase's history stays; only its current meaning goes. Forgetting a phrase that has no
 * entry, or one that is already forgotten, is a no-op rather than an error — the user
 * pressing "forget this" twice must not produce two tombstones to read back to them.
 */
export function forgetCorrection(
  entries: readonly VocabularyEntry[],
  heard: string,
  now: number,
): VocabularyEntry[] {
  const key = heard.trim().toLowerCase();
  return entries.map((e) => {
    if (e.heard.trim().toLowerCase() !== key) return e;
    if (isForgotten(e)) return e;
    const meant = currentMeaning(e) ?? '';
    return { ...e, corrections: [...e.corrections, { meant, at: now, forgotten: true as const }] };
  });
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, (m) => '\\' + m);
}

// ---------------------------------------------------------------------------
// Notifications — invitations with a hard frequency ceiling, PER CLASS
// ---------------------------------------------------------------------------

/**
 * "Notifications are invitations... with a hard frequency ceiling and one-tap
 * 'quieter, please' on every single notification" (product-concept §11).
 *
 * CLASSES EXIST BECAUSE ONE SWITCH WAS A SHIPPING BUG (critique 7.8). The policy used to
 * be a single global `{ enabled, quietLevel }`, and `quieterPlease` set
 * `enabled: false` on the third tap — so three taps of "quieter, please" on *compost
 * invitations* silently disabled the *vet-appointment reminder*, which product-concept §5
 * promises "calendar-grade treatment and escalating, user-configured reminders". The
 * quieting gesture must only quiet the kind of thing the user was looking at.
 *
 *   `deadline`  — hard external commitments (§5). PROTECTED: quieting another class can
 *                 never touch it, and quieting *it* still leaves a floor, because
 *                 calendar-grade means the reminder arrives.
 *   `surfacing` — "the barn loops are nearby if you want them" (§8.1).
 *   `compost`   — the fortnightly review invitation (§8.3).
 *   `sorting`   — the daily Sorting prompt, itself default-off (§7.2).
 */
export const NOTIFICATION_CLASSES = ['deadline', 'surfacing', 'compost', 'sorting'] as const;
export type NotificationClass = (typeof NOTIFICATION_CLASSES)[number];

/** Classes the quieting gesture may not disable. */
export const PROTECTED_NOTIFICATION_CLASSES = ['deadline'] as const;
export type ProtectedNotificationClass = (typeof PROTECTED_NOTIFICATION_CLASSES)[number];

export function isProtectedClass(cls: NotificationClass): cls is ProtectedNotificationClass {
  return (PROTECTED_NOTIFICATION_CLASSES as readonly NotificationClass[]).includes(cls);
}

export const MAX_NOTIFICATIONS_PER_DAY = 3;
/** A protected class never falls below this, however often the user asks for quiet. */
export const MIN_PROTECTED_NOTIFICATIONS_PER_DAY = 1;

export type ClassPolicy = {
  readonly enabled: boolean;
  /** 0 = default cadence. Each "quieter, please" on THIS class increments it. */
  readonly quietLevel: number;
};

export type NotificationPolicy = {
  readonly classes: Readonly<Record<NotificationClass, ClassPolicy>>;
  /** Local hours during which nothing is delivered — except a protected class. */
  readonly quietHoursFrom?: number;
  readonly quietHoursTo?: number;
};

export const DEFAULT_NOTIFICATION_POLICY: NotificationPolicy = {
  classes: {
    deadline: { enabled: true, quietLevel: 0 },
    surfacing: { enabled: true, quietLevel: 0 },
    compost: { enabled: true, quietLevel: 0 },
    /**
     * OFF, in step with `Settings.sortingPrompt` (RULINGS R36).
     *
     * product-concept §7.2: "a daily gentle prompt, **default off until the user opts in**".
     * This class delivers exactly that prompt, and it used to default ON while the prompt
     * itself defaulted off — two individually-defensible defaults that jointly rendered
     * "Sorting prompt — On" on the settings surface next to a prompt that was off. One false
     * statement, on the screen this audience reads most carefully. Turning the prompt on turns
     * this class on in the same write.
     */
    sorting: { enabled: false, quietLevel: 0 },
  },
  quietHoursFrom: 21,
  quietHoursTo: 8,
};

/**
 * IS ANY OF THIS DELIVERED? YES, SINCE RULINGS R71. Read this before you read another line.
 *
 * The history matters, because this constant is the only thing standing between the settings
 * surface and a false claim, and the claim has now been false in BOTH directions.
 *
 * While it was `false` (docs/DEVIATIONS.md D19) the honest reading was that nothing was
 * delivered: `mayNotify` had no non-test caller, `LoopDate.reminderOffsetsMs` was read by no
 * scheduler, and `expo-notifications` was not a dependency of `apps/mobile`. The surface
 * therefore described what was *configured* and said so once, in body text.
 *
 * All three of those facts have changed. R7 approved `expo-notifications`;
 * `apps/mobile/src/notifications/` now owns a scheduler that routes **every** notification
 * through `mayNotify` and `withinQuietHours`; R33's ladder is read by it; and R71 flips this
 * flag, because D19's honest sentence ("Nothing is sent yet") is now the false one.
 *
 * WHAT THE FLAG STILL DOES NOT CLAIM. It says a scheduler exists and this policy governs it.
 * It does not claim a grant: a device that has refused the permission delivers nothing, and
 * `ScheduleOutcome.blocked` is how the scheduler says so — rendered on the surface through
 * `notify.blocked.noPermission` / `notify.blocked.unavailable` (R72). A per-device fact belongs
 * in a per-device reading, never in a compile-time constant.
 *
 * `apps/mobile/test/notifications.test.ts` pins it to reality in both directions: it must be
 * `true` while a notification dependency and a scheduler module are present, and it may only
 * return to `false` if both go away.
 *
 * product-concept §5 (hard deadlines get "calendar-grade treatment and escalating,
 * user-configured reminders") and §11 ("a hard frequency ceiling and one-tap 'quieter,
 * please' on every single notification") are what this now pays.
 */
export const NOTIFICATION_DELIVERY_WIRED = true;

export function classPolicy(policy: NotificationPolicy, cls: NotificationClass): ClassPolicy {
  /**
   * Guarded, because the TYPE cannot see a missing class. A policy arriving from sync, from an
   * older build, or from a future one with a class this build does not know returns `undefined`
   * here, and `dailyNotificationBudget` then threw on `p.enabled` — on the settings surface
   * (critique 5.3). `normaliseNotificationPolicy` is the front door; this is the floor.
   */
  const found = policy.classes[cls] as ClassPolicy | undefined;
  return found ?? DEFAULT_NOTIFICATION_POLICY.classes[cls];
}

/** The loose shape a policy can arrive in from sync, storage, or a different app version. */
export type NotificationPolicyInput = {
  readonly classes?: Partial<Record<string, Partial<ClassPolicy> | undefined>>;
  readonly quietHoursFrom?: number;
  readonly quietHoursTo?: number;
};

/**
 * The normaliser this group did not have.
 *
 * `normaliseIntensity` and `normaliseReading` both exist because a value arriving "from sync,
 * from a future app version, or from a slider bug" must never produce an unrenderable state,
 * and `NotificationPolicy` was the only settings group without one (critique 5.3).
 *
 * THE INVARIANT WORTH HAVING HERE: a protected class comes back `enabled: true` whatever the
 * input said. `mayNotify` already ignores `enabled` for a protected class, so a stored `false`
 * was never obeyed — but it rendered, and a policy that *says* the vet reminder is off is the
 * same broken promise as one that acts on it (product-concept §5).
 */
export function normaliseNotificationPolicy(
  input: NotificationPolicyInput | undefined,
): NotificationPolicy {
  // NO POLICY AT ALL is different from a policy with no quiet hours: a device that has never
  // stored one gets the default window, while a stored policy whose window is absent has had it
  // cleared and must keep it cleared.
  if (input === undefined) return DEFAULT_NOTIFICATION_POLICY;

  const classes = {} as Record<NotificationClass, ClassPolicy>;
  for (const cls of NOTIFICATION_CLASSES) {
    const fallback = DEFAULT_NOTIFICATION_POLICY.classes[cls];
    const raw = input?.classes?.[cls];
    const quietLevel =
      typeof raw?.quietLevel === 'number' && Number.isFinite(raw.quietLevel)
        ? Math.max(0, Math.round(raw.quietLevel))
        : fallback.quietLevel;
    const enabled = isProtectedClass(cls)
      ? true
      : typeof raw?.enabled === 'boolean'
        ? raw.enabled
        : fallback.enabled;
    classes[cls] = { enabled, quietLevel };
  }

  // Both hours or neither: a window with one end is not a window, and `withinQuietHours`
  // already treats a half-set pair as "no quiet hours" rather than guessing the other end.
  const from = normaliseHour(input?.quietHoursFrom);
  const to = normaliseHour(input?.quietHoursTo);
  const window = from !== undefined && to !== undefined ? { quietHoursFrom: from, quietHoursTo: to } : {};

  return { classes, ...window };
}

function normaliseHour(value: number | undefined): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  const hour = Math.round(value);
  return hour >= 0 && hour <= 23 ? hour : undefined;
}

export function dailyNotificationBudget(
  policy: NotificationPolicy,
  cls: NotificationClass,
): number {
  const p = classPolicy(policy, cls);
  const floor = isProtectedClass(cls) ? MIN_PROTECTED_NOTIFICATIONS_PER_DAY : 0;
  if (!p.enabled) return floor;
  return Math.max(floor, MAX_NOTIFICATIONS_PER_DAY - p.quietLevel);
}

export function withinQuietHours(policy: NotificationPolicy, now: number): boolean {
  const { quietHoursFrom: from, quietHoursTo: to } = policy;
  if (from === undefined || to === undefined) return false;
  const hour = new Date(now).getHours();
  return from <= to ? hour >= from && hour < to : hour >= from || hour < to;
}

/**
 * A protected class ignores quiet hours and storm mode: the user configured these
 * offsets themselves for a commitment the world cares about (§5), and a vet appointment
 * at 8am is exactly the reminder a 21:00–08:00 quiet window would eat. Every other class
 * is an invitation and is fully suppressible.
 */
export function mayNotify(args: {
  policy: NotificationPolicy;
  class: NotificationClass;
  now: number;
  /** Deliveries already made today IN THIS CLASS. */
  deliveredToday: number;
  /** Storm mode suppresses invitations: the user has declared today is triage. */
  stormMode?: boolean;
}): boolean {
  const protectedClass = isProtectedClass(args.class);
  if (!protectedClass) {
    if (args.stormMode) return false;
    if (!classPolicy(args.policy, args.class).enabled) return false;
    if (withinQuietHours(args.policy, args.now)) return false;
  }
  return args.deliveredToday < dailyNotificationBudget(args.policy, args.class);
}

/**
 * One tap, on the notification the user is looking at. It quiets THAT class only.
 *
 * `quietLevel` rises and never falls on its own — nothing in Carn is ever taken away by
 * time, and that cuts both ways: a preference the user expressed is not quietly forgotten
 * so we can notify them again.
 */
export function quieterPlease(
  policy: NotificationPolicy,
  cls: NotificationClass,
): NotificationPolicy {
  const current = classPolicy(policy, cls);
  const quietLevel = current.quietLevel + 1;
  // Past the ceiling, "quieter" means off — for an invitation. A protected class stays
  // on at its floor, because the alternative is a missed vet appointment.
  const enabled = isProtectedClass(cls) ? true : quietLevel < MAX_NOTIFICATIONS_PER_DAY;
  const classes: Record<NotificationClass, ClassPolicy> = { ...policy.classes };
  classes[cls] = { enabled, quietLevel };
  return { ...policy, classes };
}

/**
 * THE WAY BACK. One tap, on the class the user is looking at: it lets THAT class reach them a
 * little more often, and touches nothing else.
 *
 * WHY THIS EXISTS, given the sentence directly above it. `quieterPlease`'s doc says `quietLevel`
 * "rises and never falls **on its own**", and that qualifier is the whole of the rule: nothing in
 * Carn is ever taken away BY TIME (design-direction §6, objection 1), and no code path may quietly
 * forget a preference so the app can notify more. Neither of those is what this is. This is the
 * user, at the settings surface, stating a new preference — and product-concept §11 makes sensory
 * control the user's ("Full sensory settings"), which a one-way ratchet is not.
 *
 * It was one-way in fact as well as in principle: `quieterPlease` was reachable from every
 * notification and from every class block, `quietLevel` had no other writer, and
 * `normaliseNotificationPolicy` carries it across every read — so three taps on the lock screen
 * left a class at zero a day with nothing anywhere in the product that could raise it again. The
 * only escape was the on/off switch, which does not clear `quietLevel` (deliberately, R36), so a
 * class turned off and on again came back silent. That is a control that fights back.
 *
 * EXACTLY THE INVERSE OF `quieterPlease`, so the two cannot drift:
 *   - `quietLevel` falls by one and floors at 0 — never negative, so `dailyNotificationBudget`
 *     can never exceed `MAX_NOTIFICATIONS_PER_DAY`. There is no "louder than default".
 *   - It re-enables an invitation that quieting had switched off, because the tap means "I want
 *     to hear from this again" and leaving `enabled: false` under a raised budget would be the
 *     same two-true-facts-one-false-statement shape R36 settled.
 *   - A protected class is left `enabled: true`, which it always was.
 */
export function louderPlease(
  policy: NotificationPolicy,
  cls: NotificationClass,
): NotificationPolicy {
  const current = classPolicy(policy, cls);
  const quietLevel = Math.max(0, current.quietLevel - 1);
  const classes: Record<NotificationClass, ClassPolicy> = { ...policy.classes };
  classes[cls] = { enabled: true, quietLevel };
  return { ...policy, classes };
}

/**
 * Is there any quiet left to give back on this class?
 *
 * The surface asks so it can HIDE the control rather than disable it where there is nothing to
 * undo (docs/RULINGS.md R12 prefers absence to disablement) — a class already at the default
 * cadence and already on has nothing this gesture would change.
 */
export function canBeLouder(policy: NotificationPolicy, cls: NotificationClass): boolean {
  const current = classPolicy(policy, cls);
  if (current.quietLevel > 0) return true;
  return !isProtectedClass(cls) && !current.enabled;
}

// ---------------------------------------------------------------------------
// Storm mode
// ---------------------------------------------------------------------------

/**
 * "A user-declared 'storm mode' (chaotic day — everything is triage, judgment
 * suspended, cleanup deferred to the next Sorting session)" — product-concept §5,
 * objection 2.
 *
 * It expires on its own, because a storm the user forgot to turn off should not silently
 * become their permanent settings. Expiry is not a punishment and produces no
 * notification.
 *
 * MINIMUM DURATION (critique 1.7). Expiry used to be the end of the declared local day,
 * so a storm declared at 23:50 — 11pm on a bad day, the exact scenario
 * design-direction §2 names — lasted ten minutes and then silently re-imposed the cap and
 * the compost invitations. A declared storm now always lasts at least
 * `STORM_MIN_DURATION_MS`.
 */
export type StormMode = { readonly declaredAt: number };

export const STORM_MIN_DURATION_MS = 12 * 60 * 60 * 1000;

export function stormExpiresAt(storm: StormMode): number {
  const endOfDeclaredDay = new Date(storm.declaredAt).setHours(23, 59, 59, 999);
  return Math.max(endOfDeclaredDay, storm.declaredAt + STORM_MIN_DURATION_MS);
}

export function stormActive(storm: StormMode | undefined, now: number): boolean {
  if (!storm) return false;
  return now <= stormExpiresAt(storm);
}

// ---------------------------------------------------------------------------
// The settings shape
// ---------------------------------------------------------------------------

/**
 * The four preference groups below were anonymous inline shapes until `packages/schema` needed
 * to give each of them a column (RULINGS R66/R80, the critique's S2.9). Naming them is the
 * whole change — a schema module cannot type a `jsonb` column against a shape with no name, and
 * re-declaring the shape there is exactly the drift R66 exists to close.
 */
export const COLOUR_SCHEMES = ['light', 'dark', 'system'] as const;
export type ColourScheme = (typeof COLOUR_SCHEMES)[number];

export const DENSITIES = ['sparse', 'balanced', 'full'] as const;
export type Density = (typeof DENSITIES)[number];

/**
 * How a control renders its glyph and its word.
 *
 * Authorised by design-direction §6 ("a single consistent stroke weight, literal-first
 * forms... and every icon paired with a text label by default — icons-only is an opt-in
 * density choice, never the default — icon-guessing is executive load") and CLAUDE.md
 * ("Every icon has a text label by default. Icons-only is an opt-in density choice.").
 *
 * THE CONFLICT THIS TYPE RECORDS (docs/OPEN-QUESTIONS.md, product-owner request logged
 * there). The product owner asked for icon-only as the DEFAULT on controls like back and
 * close. Both laws above forbid shipping that default, so this is a three-way opt-in
 * setting instead — the panel-governed resolution is written up in full at
 * docs/OPEN-QUESTIONS.md rather than decided silently here.
 *
 * `words-only` is the maximally-safe end of the SAME axis §6 is already about
 * (icon-guessing as executive load): removing the glyph entirely costs nothing a
 * literal-minded or low-vision reader needs, so it is one more step along an
 * already-authorised opt-in choice, not a fourth, unauthorised one — see
 * docs/DEVIATIONS.md for the entry recording that argument.
 */
export const LABEL_DENSITIES = ['icon-and-words', 'words-only', 'icon-only'] as const;
export type LabelDensity = (typeof LABEL_DENSITIES)[number];

/**
 * design-direction §4's "family alternates" lever, declared here for the reason
 * `LABEL_DENSITIES` and the intensity channels are (R29): a settings vocabulary with two
 * declarations eventually disagrees with itself, and an unknown family must not be able to
 * persist silently. `@carn/tokens` imports this union and owns only how each name RENDERS.
 *
 * WHAT §4 AUTHORISES, AND WHAT IT DOES NOT (docs/DEVIATIONS.md F1). §4 names "family
 * alternates" as one of the dyslexia-oriented levers, in the same sentence as letter spacing
 * and weight — both of which already shipped in `ReadingSettings`. It ALSO fixes "a highly
 * legible humanist sans as the single UI family", and §4 objection 2 rejects a second family
 * on purpose. `'sans'` therefore stays the single UI family and the shipped default; the
 * alternates are reachable only from the reading section. The residual question — whether an
 * alternate family belongs there at all — is the ND panel's, and is `RULINGS.md` Part 9
 * question 4; docs/OPEN-QUESTIONS.md carries it in §7's falsifiable form.
 *
 * `serif` is the reader's OWN platform serif, never a bundled brand face, so §4's "no display
 * serif, no novelty face" — which governs Carn's typographic voice — is untouched by it.
 *
 * `comic` is **Comic Neue**, the OFL Comic Sans-alike, added on a product-owner request
 * (docs/DEVIATIONS.md F3). It is the member most obviously in tension with §4's "no novelty
 * face", and it survives on the same division every member here does: that clause selects the
 * SINGLE UI FAMILY, which is `sans`, and this is a reading lever a user has to go and choose.
 * Comic Sans MS itself is Microsoft's and cannot be shipped, so the option is named for the
 * face it actually draws rather than the one it resembles.
 *
 * `system` is whatever the reader's own device uses for its interface, and it is the one member
 * that ships no opinion at all (docs/DEVIATIONS.md F4). It exists because a user who has already
 * set a font at OS level — an accessibility choice we cannot see and must not override — has no
 * other way to say "use that". It is also the only family whose numerals are tabular on iOS and
 * Android, which is why F1 objection 4's unresolved §4 problem has an escape hatch.
 */
export const READING_FAMILIES = ['sans', 'system', 'serif', 'dyslexic', 'comic'] as const;
export type ReadingFamily = (typeof READING_FAMILIES)[number];

export function isReadingFamily(value: unknown): value is ReadingFamily {
  return READING_FAMILIES.includes(value as ReadingFamily);
}

export type ReadingSettings = {
  readonly textScale: number;
  readonly letterSpacing: number;
  readonly lineHeightBoost: number;
  readonly bolderText: boolean;
  readonly fontFamily: ReadingFamily;
};

export type SoundSettings = { readonly enabled: boolean; readonly volume: number };

export type Settings = {
  /**
   * The Intensity dial and its per-channel mixer, TYPED (RULINGS R29).
   *
   * This used to be `{ dial: number; overrides: Record<string, number | 'auto'> }` — loose,
   * with a comment saying the channel names wanted one shared declaration. They now have one,
   * in `./intensity.ts`, which `@carn/tokens` imports rather than redeclaring. The
   * consequence that matters: an unknown channel name can no longer persist silently.
   */
  readonly intensity: IntensityPreferences;
  readonly scheme: ColourScheme;
  readonly density: Density;
  /**
   * How every eligible control renders its glyph and its word (design-direction §6,
   * CLAUDE.md; see `LabelDensity`'s own doc comment for the product-owner conflict this
   * setting resolves). Applies to navigation chrome and repeated controls — back, close,
   * tab bar, add, remove, share, undo. Does NOT apply to a control whose words are
   * load-bearing (a destructive confirmation, the Interrupt Button, first-run, or
   * anything that is the only explanation of a consequence) — those opt out explicitly,
   * at the call site, rather than reading this field.
   */
  readonly labels: LabelDensity;
  /**
   * WHETHER A LIST SHOWS A SECTION THAT HAS NOTHING IN IT (RULINGS R132).
   *
   * TRUE by default, and the default is the load-bearing half: an empty "Dad" on a gift list is
   * the user's own structure, and a section that vanished the moment they made it is the failure
   * `listGroups`' own `includeEmpty` note and R125 both describe. This field never changes that
   * — it only lets a user who has asked for it read their list the other way.
   *
   * IT IS THE SAME AXIS AS `density` AND `labels` — how much shows — and design-direction §4
   * authorises exactly that axis ("a user-controlled density setting exists… but higher densities
   * are opt-in and never the default"). It runs in the opposite direction to the home surface's,
   * where MORE is the opt-in, and that inversion is recorded in `docs/DEVIATIONS.md`
   * §empty-sections rather than waved through, because §4's parenthesis — "hiding can mean
   * ceasing-to-exist" — is an argument against this setting and had to be answered.
   *
   * WHAT IT DOES NOT REACH. Shopping mode never showed empty sections (`shoppingModeGroups`,
   * §8.2), and the section manager always shows every section whatever this says — that is what
   * keeps a hidden section renameable, removable and reachable rather than gone.
   */
  readonly showEmptySections: boolean;
  /**
   * The selected `MaterialId` (design-direction §2, §7). Free by default (`drystone`);
   * a paid choice that has lapsed is not overwritten here — `effectiveMaterial` in
   * `materials.ts` is where entitlement meets this stored choice, so a lapsed
   * subscriber's pick waits for them rather than being erased (docs/DEVIATIONS.md M11).
   */
  readonly material: MaterialId;
  /**
   * The surface treatment (`RULINGS R188`) — how a card's field is rasterised, orthogonal to
   * which colours it uses. `matte` is the default and is the pre-treatment rendering.
   *
   * FLAT, NOT A PREFERENCE GROUP, for the reason stated two fields up about `material` and
   * `labels`: it is a single closed-set value. And unlike `material` there is no entitlement
   * seam for it to pass through — `treatments.ts` records why a paid treatment would make the
   * four paid materials illegal under `docs/DEVIATIONS.md` M11.
   */
  readonly treatment: Treatment;
  /**
   * The home-screen app icon (`RULINGS R197`). Same closed-set, flat-column shape as `material`
   * and `treatment`, and the same type as `material` — the icon set IS the material set,
   * rendered as artwork (`appIcons.ts`).
   *
   * IT IS STORED HERE, AND THE PLATFORM IS STILL THE SOURCE OF TRUTH FOR WHAT IS ON SCREEN.
   * iOS owns the live icon: it survives app deletion of state, it is not readable before the
   * first launch, and the user can never be shown a picker that disagrees with their home
   * screen. So this field records the user's INTENT and `src/native/appIcon.ts` reconciles it
   * against `UIApplication.alternateIconName` on launch. The two can legitimately differ for one
   * moment — a device that synced a choice made on another phone — and reconciling is what
   * closes it.
   *
   * NO ENTITLEMENT SEAM, deliberately: unlike `material` there is no `effectiveAppIcon`, because
   * every icon is free at every tier. `appIcons.ts` carries the argument.
   */
  readonly appIcon: AppIcon;
  /** product-concept §11: the literal-language mode. */
  readonly literalLanguage: boolean;
  readonly reading: ReadingSettings;
  readonly sound: SoundSettings;
  /**
   * product-concept §6, objection 3: capsule imagery is optional. The type is
   * `CapsuleImagery` from model.ts — the same type `visibleCapsuleFields` consumes, so
   * the two cannot disagree on shape the way they did before (critique).
   */
  readonly imagery: CapsuleImagery;
  readonly warmCap: number;
  /**
   * WHAT A WIDGET MAY DRAW WHERE (docs/specs/widgets-architecture.md §5).
   *
   * A single closed-set value, so it is a FLAT field rather than a preference group — the
   * same shape, and the same argument, as `labels` and `material` two fields up
   * (`packages/schema`'s `SettingsRow` states it plainly: flat, not jsonb, "because it is a
   * single closed-set value, not a preference group").
   *
   * Defaults to `redact-on-lock`: product-concept §14 names photos of the user's home among
   * the most sensitive data classes and implementation-brief §3 makes on-device-only a
   * privacy invariant with force of law, while product-concept §6.2 requires the Interrupt
   * Button to stay "always-reachable" — so content is withheld on the lock screen and the
   * control keeps working. `normaliseWidgetPrivacy` is the boundary function, and it falls
   * back to this default rather than to the permissive end.
   *
   * **WHICH WIDGETS ARE PLACED IS NOT A SETTING AND MUST NOT BECOME ONE.** The OS owns
   * placement: a person adds a widget from the OS gallery, moves it, and removes it without
   * the app being told. A per-kind "enabled" switch here would be a shadow registry of
   * something this process cannot observe — it would render "Interrupt widget — On" beside a
   * home screen that has never had one, which is exactly the single-false-statement shape
   * RULINGS R36 settled, on the screen this audience reads most carefully. The app writes a
   * snapshot for every kind unconditionally and lets the OS draw whichever ones exist.
   */
  readonly widgetPrivacy: WidgetPrivacy;
  readonly storm?: StormMode;
  readonly notifications: NotificationPolicy;
  readonly energy: EnergySettings;
  /** Sorting session prompt is DEFAULT OFF until the user opts in (§7.2). */
  readonly sortingPrompt: { readonly enabled: boolean; readonly hourOfDay: number };
  /**
   * The daily suggestion card, DEFAULT OFF (`DEFAULT_TODAY`, docs/DEVIATIONS.md TD1).
   *
   * Same shape of fact as `sortingPrompt` above and defaulted off for the same reason: it is
   * the app asking for a moment of the user's day, and product-concept §3 refuses anything
   * that arrives uninvited and reads as a daily goal. `nextToday` in `./today.ts` is the only
   * function that may build a value for this field — it clamps the hour and clears the
   * dismissal stamp on the off-write, which is the coupling `setSortingPrompt` has for its
   * notification class. This setting deliberately has NO notification class.
   */
  readonly today: TodaySettings;
  /**
   * Whether Sorting offers suggestions at all. False = fully manual, which product-concept
   * §7.2 objection 2 makes a trust requirement ("Sorting can be turned fully manual").
   * Nothing degrades when it is off: the user approves their own words.
   */
  readonly sortingSuggestions: boolean;
  readonly compost: {
    readonly reviewIntervalDays: number;
    readonly lastReviewAt?: number;
    /**
     * When the user last said "not now" to a review invitation (§8.3: "nothing ever demands
     * review"). A SEPARATE fact from `lastReviewAt` — a declined invitation is not a review,
     * so the fortnightly rhythm keeps its own clock. `shouldInviteReview` reads it.
     */
    readonly lastInviteDeclinedAt?: number;
  };
  readonly places: readonly Place[];
  readonly vocabulary: readonly VocabularyEntry[];
  /** When the user first opened Carn. Drives `shouldAskEnergy`. */
  readonly firstRunAt?: number;
  /** When the return greeting was last shown (product-concept §11). */
  readonly lastReturnGreetingAt?: number;
};

export const DEFAULT_SETTINGS: Settings = {
  // The dial at Steady, with the `voice` channel pinned to plain words — see
  // `DEFAULT_VOICE_LEVEL` for why the wording default is a pin on the channel and not the
  // boolean below (docs/DEVIATIONS.md D30).
  intensity: DEFAULT_INTENSITY_PREFERENCES,
  scheme: 'system',
  density: 'sparse',
  // ICON-AND-WORDS. design-direction §6 and CLAUDE.md both fix this as the default —
  // icons-only is an opt-in density choice, never the default — so the setting exists
  // but does not start there, whatever a product request asked for (see `LabelDensity`).
  labels: 'icon-and-words',
  // TRUE — every section shows, including the empty ones. R125's "a section vanished the moment
  // they made it" is what this default refuses; hiding is the opt-in (RULINGS R132).
  showEmptySections: true,
  material: DEFAULT_MATERIAL,
  // MATTE — the rendering that shipped before treatments existed, from `./treatments.ts`
  // rather than a second literal, the rule the intensity and material defaults follow.
  treatment: DEFAULT_TREATMENT,
  // DRYSTONE — which is the icon already in the bundle, so a device that has never chosen shows
  // exactly what it showed before this setting existed. From `./appIcons.ts` rather than a second
  // literal, the rule every default above follows.
  appIcon: DEFAULT_APP_ICON,
  /**
   * FALSE, AND THE APP STILL SHIPS PLAIN WORDS. This flag is the user's explicit PIN on the
   * literal register, not the register itself: `intensity.overrides.voice` above is what makes a
   * new device read functional labels, and it is what the wording control and the mixer both
   * write. Defaulting this to `true` instead would render identically and leave the mixer's
   * Wording row inert — see `DEFAULT_VOICE_LEVEL` (product-concept §11, design-direction §3).
   */
  literalLanguage: false,
  reading: {
    textScale: 1,
    letterSpacing: 0,
    lineHeightBoost: 1,
    bolderText: false,
    // design-direction §4 objection 2's single UI family, as the shipped default.
    fontFamily: 'sans',
  },
  sound: { enabled: false, volume: 0.5 },
  imagery: { capsulePhotos: true },
  warmCap: 5,
  // Content withheld on the lock screen, controls still live. From `./widgets.ts` rather
  // than a second literal, the rule the intensity, material and today defaults follow, so
  // the two cannot drift.
  widgetPrivacy: DEFAULT_WIDGET_PRIVACY,
  notifications: DEFAULT_NOTIFICATION_POLICY,
  energy: { asked: false },
  // Default off. Demanding a daily triage ritual of this audience on day one is how
  // the app becomes another thing they are behind on (§7.2).
  sortingPrompt: { enabled: false, hourOfDay: 19 },
  // Default off, from `./today.ts` rather than a second literal — the same rule the
  // intensity and material defaults follow, so the two cannot drift.
  today: DEFAULT_TODAY,
  sortingSuggestions: true,
  compost: { reviewIntervalDays: 14 },
  places: [],
  vocabulary: [],
};

/**
 * Turn the daily Sorting prompt on or off — AND THE NOTIFICATION CLASS THAT DELIVERS IT, IN THE
 * SAME WRITE (RULINGS R36).
 *
 * R36's build consequence has two halves. The default was fixed and the coupling was not, so the
 * two agreeing was a property of `DEFAULT_SETTINGS` rather than a property of the system: the
 * first writer to land — R46 restores the switch the Sorting screen removed — would have set
 * `sortingPrompt.enabled` and left `classes.sorting` wherever it was, reintroducing the exact
 * divergence R36 closed, and the test that exists would still have passed because it asserts the
 * defaults rather than the invariant (the critique's S3.3).
 *
 * So the coupling lives here, in the only writer, rather than in a screen. product-concept §7.2:
 * "a daily gentle prompt, default off until the user opts in" — one fact, one write. A settings
 * surface that renders "Sorting prompt — On" beside a class that delivers nothing is one false
 * statement on the screen this audience reads most carefully.
 *
 * `quietLevel` is deliberately CARRIED THROUGH, not reset. Every "quieter, please" the user has
 * ever said about this class is a fact they stated (§11), and turning the prompt off and on again
 * is not a retraction of it — resetting would be time taking something away.
 */
export function setSortingPrompt(
  settings: Settings,
  next: { readonly enabled: boolean; readonly hourOfDay?: number },
): Settings {
  const hourOfDay = next.hourOfDay ?? settings.sortingPrompt.hourOfDay;
  const existing = classPolicy(settings.notifications, 'sorting');
  return {
    ...settings,
    sortingPrompt: { enabled: next.enabled, hourOfDay },
    notifications: {
      ...settings.notifications,
      classes: {
        ...settings.notifications.classes,
        sorting: { enabled: next.enabled, quietLevel: existing.quietLevel },
      },
    },
  };
}
